import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';

import type { BrandData } from 'src/clients/OnboardingClient';
import { ClientType } from 'src/constants';
import { OnboardingLoginFormValues } from 'src/legacy/components/Onboarding/OnboardingForms';
import { IndustryOptionType } from 'src/routes/onboarding/setup/components/about-your-company/constants';
import {
  STUDIO_STEP_EMAIL_VERIFICATION,
  STUDIO_STEP_WORKSPACE_SETUP,
} from 'src/routes/onboarding/setup/constants';

const ONBOARDING_STEPS = [
  'create-account',
  'login-challenge',
  'company-setup',
  'about-your-company',
  'who-you-serve',
  'customize-your-workspace',
] as const;

export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

export function isOnboardingStep(
  step: string | undefined,
): step is OnboardingStep {
  return ONBOARDING_STEPS.includes(step as OnboardingStep);
}

export type TrackedEventsType = Partial<Record<OnboardingStep, boolean>>;

const STUDIO_STEPS = [
  STUDIO_STEP_EMAIL_VERIFICATION,
  STUDIO_STEP_WORKSPACE_SETUP,
] as const;

/** Studio funnel steps that can be resumed via the `step` query param. */
export type StudioResumeStep = (typeof STUDIO_STEPS)[number];

export function isStudioStep(
  step: string | undefined,
): step is StudioResumeStep {
  return STUDIO_STEPS.includes(step as StudioResumeStep);
}

export interface OnboardingQueryParams {
  referred?: string;
  source?: string;
  prefill_email?: string;
  name?: string;
  template_id?: string;
  template_name?: string;
  step?: string;
  authSource?: CognitoHostedUIIdentityProvider;
  referrer?: string;
  prompt?: string;
  /**
   * Marketing → App Builder template handoff. Source app id for
   * POST /app-builders/clone { templateId }. Not the classic
   * workspace-template `template_id` (snake_case).
   */
  templateId?: string;
  /**
   * Display-only title for the Studio Get started arrival card. Read from the
   * marketing landing URL; not preserved across OAuth / cross-nav hops.
   */
  templateName?: string;
  /**
   * Display-only description for the Studio Get started arrival card. Read from
   * the marketing landing URL; not preserved across OAuth / cross-nav hops.
   */
  templateDescription?: string;
  /** Studio Google post-create callback — see studioGooglePostCreate.ts */
  studio_post_create?: string;
  portal_header?: string;
  portal_url?: string;
  next?: string;
  /** Cognito Hosted UI authorization code (present during OAuth return). */
  code?: string;
  /** Amplify OAuth state (`<random>-<hexCustomState>`). */
  state?: string;
}

export interface OnboardingFormValues extends OnboardingLoginFormValues {
  referred?: string;
  source?: string;
  name: string;
  phone: string;
  foundBy: string;
  foundByCustom: string;
  companyName: string;
  portalUrl: string;
  industry: IndustryOptionType | '';
  industryLabel: string;
  companySize: string;
  companySizeLabel: string;
  signoutRedirectURL: string;
  anonymousId: string;
  clientType: ClientType | '';
  clientTypeLabel: string;
  clientCount: string;
  clientCountLabel: string;
  // used for google auth to represent the auth provider
  loginSource?: CognitoHostedUIIdentityProvider;
  picture?: string;
  workspaceTemplateId?: string;
  companyAge: string;
  companyAgeLabel: string;
  clientSource: string;
  clientSourceLabel: string;
  companyRevenue: string;
  companyRevenueLabel: string;
  priorities: string[];
  prioritiesLabels: string;
  enabledFeatures: string[];
  isAssemblyBrandEnabled: boolean;
  challengeResponse: string;
  brandData: BrandData | null;
}

type RemoveProperties<T, K extends keyof T> = Omit<T, K> & {
  [P in K]?: never;
};

/**
 * This interface corresponds directly to the data being passed to the call to /portal/any/setup.
 * This ensures that any data the BE doesn't need will have to be removed before the call is made.
 */
export type PortalSetupInput = RemoveProperties<
  OnboardingFormValues,
  | 'foundByCustom' // only used in the FE for user-defined foundBy values, if it has a value it's passed to the BE in foundBy
  | 'challengeResponse' // only used for an auth challenge like MFA
>;

export interface StudioWorkspaceSetupFormValues {
  name: string;
  companyName: string;
  portalUrl: string;
  foundBy: string;
}

/** Minimal payload for Studio workspace creation — classic onboarding fields omitted. */
export interface StudioPortalSetupInput {
  name: string;
  companyName: string;
  portalUrl: string;
  email: string;
  password?: string;
  loginSource?: CognitoHostedUIIdentityProvider;
  picture?: string;
  source?: string;
  anonymousId?: string;
  signoutRedirectURL: string;
  /** True when workspace creation is triggered from the Studio marketing entry. */
  isStudioUser?: boolean;
  /**
   * Which Studio entry the signup came through. Lets the backend set the
   * workspace group property the `studio-pricing` flag targets. Flag targeting
   * only — analytics treats both entries as Studio, and billing keys on
   * `isStudioUser`.
   */
  studioReferrer?: string;
  initialPrompt?: string;
  workspaceTemplateId?: string;
  foundBy?: string;
}
